import { Fragment, useCallback, useEffect, useState } from 'react';
import { ApiFout, api } from '@/lib/api';
import type { FoutRegel, FoutenLogAntwoord } from '@/lib/api-types';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Copy,
  Loader2,
  RefreshCw,
  Search,
} from 'lucide-react';

/**
 * Het foutenlog.
 *
 * Wat dit scherm laat zien staat nergens anders: `details` is de onbewerkte tekst
 * van de bron -- het antwoord van Azure, de melding van de database, de stack --
 * en die gaat bewust nooit naar een gebruiker. Het is precies wat je nodig hebt om
 * te onderzoeken wat er misgaat, en het is ook de reden dat dit tabblad achter de
 * beheerdersrol zit.
 *
 * Het filteren gebeurt op de server en niet hier. Een foutenlog groeit in een
 * slechte week hard -- één Azure-storing levert een regel per aanroep op -- en dan
 * is "alles ophalen en in de browser filteren" het verschil tussen een scherm dat
 * opent en een dat vastloopt. Dat is ook waarom de codelijst uit de API komt: die
 * telt over de hele tabel, niet over de pagina die toevallig geladen is.
 */

const PAGINA = 100;

/** Leesbare namen voor de bronnen uit lib/error-log.ts. */
const BRON_LABELS: Record<string, string> = {
  ai_soep: 'SOEP-generatie',
  ai_icpc: 'ICPC-suggestie',
  ai_translate: 'Vertaling',
  ai_test: 'AI-test (beheer)',
  patient_translate: 'Vertaling patiënt-mobiel',
  patient_pdf: 'Patiëntsamenvatting (PDF)',
  speech_token: 'Spraaktoken',
  speech_tts: 'Tekst naar spraak',
  onbehandeld: 'Onbehandeld',
};

/** Alles wat op een tijdslimiet duidt; die krijgen een eigen kleur in de tabel. */
function isTimeout(code: string): boolean {
  return code.endsWith('_timeout');
}

function formatTijd(waarde: string): string {
  const dt = new Date(waarde);
  return `${dt.toLocaleDateString('nl-NL')} ${dt.toLocaleTimeString('nl-NL', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })}`;
}

export function ErrorLogsTab() {
  const [entries, setEntries] = useState<FoutRegel[]>([]);
  const [codes, setCodes] = useState<Array<{ code: string; count: number }>>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const [zoek, setZoek] = useState('');
  /** Wat er daadwerkelijk naar de server gaat; loopt achter op `zoek`. */
  const [zoekTerm, setZoekTerm] = useState('');
  const [code, setCode] = useState('all');
  const [van, setVan] = useState('');
  const [tot, setTot] = useState('');

  const [openId, setOpenId] = useState<string | null>(null);

  // Niet bij elke toetsaanslag een query: het zoeken gaat over `details`, en dat is
  // een ilike over de grootste kolom van de tabel.
  useEffect(() => {
    const t = setTimeout(() => {
      setZoekTerm(zoek.trim());
      setOffset(0);
    }, 350);
    return () => clearTimeout(t);
  }, [zoek]);

  const laden = useCallback(async () => {
    setIsLoading(true);
    try {
      const antwoord = await api.get<FoutenLogAntwoord>('/admin/errors', {
        query: {
          limit: PAGINA,
          offset,
          ...(zoekTerm ? { q: zoekTerm } : {}),
          ...(code !== 'all' ? { code } : {}),
          ...(van ? { from: van } : {}),
          ...(tot ? { to: tot } : {}),
        },
      });
      setEntries(antwoord.entries);
      setCodes(antwoord.codes);
      setTotal(antwoord.total);
    } catch (fout) {
      toast.error(fout instanceof ApiFout ? fout.message : 'Kon het foutenlog niet laden');
    } finally {
      setIsLoading(false);
    }
  }, [offset, zoekTerm, code, van, tot]);

  useEffect(() => {
    void laden();
  }, [laden]);

  const kopieer = async (regel: FoutRegel) => {
    const tekst = [
      `Tijd:      ${formatTijd(regel.createdAt)}`,
      `Code:      ${regel.code}${regel.statusCode ? ` (HTTP ${regel.statusCode})` : ''}`,
      `Bron:      ${BRON_LABELS[regel.source] ?? regel.source}`,
      `Route:     ${regel.route ?? '—'}`,
      `Praktijk:  ${regel.practiceName ?? '—'}`,
      `Gebruiker: ${regel.userName ?? '—'}`,
      `RequestId: ${regel.requestId ?? '—'}`,
      `Melding:   ${regel.message}`,
      '',
      regel.details ?? '(geen onbewerkte fouttekst)',
    ].join('\n');

    try {
      await navigator.clipboard.writeText(tekst);
      toast.success('Foutmelding gekopieerd');
    } catch {
      // Zonder toestemming voor het klembord valt er niets te kopiëren; dat is geen
      // reden voor een foutmelding die op een storing lijkt.
      toast.error('Kopiëren is niet toegestaan in deze browser');
    }
  };

  const eersteVanPagina = total === 0 ? 0 : offset + 1;
  const laatsteVanPagina = Math.min(offset + entries.length, total);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" />
          Foutenlog
        </CardTitle>
        <CardDescription>
          Mislukte aanroepen met de onbewerkte foutmelding van de bron. Bewaartermijn 90 dagen.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-end gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Zoek in de melding en de fouttekst..."
              value={zoek}
              onChange={(e) => setZoek(e.target.value)}
              className="pl-9"
            />
          </div>

          <Select
            value={code}
            onValueChange={(waarde) => {
              setCode(waarde);
              setOffset(0);
            }}
          >
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="Code" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle codes</SelectItem>
              {codes.map((c) => (
                <SelectItem key={c.code} value={c.code}>
                  {c.code} ({c.count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground" htmlFor="fout-van">
              Van
            </label>
            <Input
              id="fout-van"
              type="date"
              className="w-[150px]"
              value={van}
              onChange={(e) => {
                setVan(e.target.value);
                setOffset(0);
              }}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground" htmlFor="fout-tot">
              Tot en met
            </label>
            <Input
              id="fout-tot"
              type="date"
              className="w-[150px]"
              value={tot}
              onChange={(e) => {
                setTot(e.target.value);
                setOffset(0);
              }}
            />
          </div>

          <Button variant="outline" size="sm" onClick={() => void laden()} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
            Vernieuwen
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="text-xs text-muted-foreground">
              {total === 0
                ? 'Geen fouten gevonden'
                : `${eersteVanPagina}–${laatsteVanPagina} van ${total} fouten`}
            </div>

            <div className="border rounded-md overflow-hidden">
              <div className="overflow-x-auto max-h-[560px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0">
                    <tr className="bg-muted">
                      <th className="w-6 p-2" aria-label="Uitklappen" />
                      <th className="text-left p-2 font-medium">Tijdstip</th>
                      <th className="text-left p-2 font-medium">Praktijk</th>
                      <th className="text-left p-2 font-medium">Gebruiker</th>
                      <th className="text-left p-2 font-medium">Bron</th>
                      <th className="text-left p-2 font-medium">Code</th>
                      <th className="text-left p-2 font-medium">Melding</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="p-4 text-center text-muted-foreground">
                          Geen fouten gevonden
                        </td>
                      </tr>
                    ) : (
                      entries.map((regel) => {
                        const isOpen = openId === regel.id;
                        const klapUit = () => setOpenId(isOpen ? null : regel.id);

                        return (
                          <Fragment key={regel.id}>
                            <tr
                              className="border-t hover:bg-muted/50 cursor-pointer"
                              aria-expanded={isOpen}
                              tabIndex={0}
                              onClick={klapUit}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  klapUit();
                                }
                              }}
                            >
                              <td className="p-2 text-muted-foreground">
                                {isOpen ? (
                                  <ChevronDown className="h-3.5 w-3.5" />
                                ) : (
                                  <ChevronRight className="h-3.5 w-3.5" />
                                )}
                              </td>
                              <td className="p-2 whitespace-nowrap font-mono">
                                {formatTijd(regel.createdAt)}
                              </td>
                              <td className="p-2">{regel.practiceName ?? '—'}</td>
                              <td className="p-2">{regel.userName ?? '—'}</td>
                              <td className="p-2">{BRON_LABELS[regel.source] ?? regel.source}</td>
                              <td className="p-2">
                                <span
                                  className={`font-mono rounded px-1.5 py-0.5 ${
                                    isTimeout(regel.code)
                                      ? 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200'
                                      : 'bg-destructive/10 text-destructive'
                                  }`}
                                >
                                  {regel.code}
                                </span>
                              </td>
                              <td className="p-2 max-w-[340px] truncate" title={regel.message}>
                                {regel.message}
                              </td>
                            </tr>

                            {isOpen && (
                              <tr className="border-t bg-muted/30">
                                <td />
                                <td colSpan={6} className="p-3 space-y-3">
                                  <div className="grid grid-cols-2 gap-x-6 gap-y-1 md:grid-cols-4">
                                    <Veld naam="Route" waarde={regel.route} />
                                    <Veld
                                      naam="HTTP-status"
                                      waarde={regel.statusCode === null ? null : String(regel.statusCode)}
                                    />
                                    <Veld naam="Request-id" waarde={regel.requestId} mono />
                                    <Veld naam="Praktijk-id" waarde={regel.practiceId} mono />
                                  </div>

                                  <div>
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="text-xs font-medium">
                                        Onbewerkte fouttekst van de bron
                                      </span>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          void kopieer(regel);
                                        }}
                                      >
                                        <Copy className="h-3.5 w-3.5 mr-1" />
                                        Kopieer
                                      </Button>
                                    </div>
                                    <pre className="text-[11px] leading-relaxed whitespace-pre-wrap break-words bg-background border rounded p-2 max-h-72 overflow-y-auto">
                                      {regel.details ?? 'Geen onbewerkte fouttekst vastgelegd.'}
                                    </pre>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGINA))}
              >
                Vorige
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={offset + entries.length >= total}
                onClick={() => setOffset(offset + PAGINA)}
              >
                Volgende
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Veld({
  naam,
  waarde,
  mono,
}: {
  naam: string;
  waarde: string | null;
  mono?: boolean;
}) {
  return (
    <div>
      <div className="text-[11px] text-muted-foreground">{naam}</div>
      <div className={mono ? 'font-mono break-all' : ''}>{waarde ?? '—'}</div>
    </div>
  );
}
